import { LoggerService } from "@/shared/services/LoggerService";

import { GeoBounds } from "./geoBounds";

/**
 * Coordinates of one map path, accumulated as the worker streams them in and
 * read back by index, by log time, and by geographic bounds. Points live in
 * three parallel typed arrays rather than one array of objects, so a path of
 * hundreds of thousands of points is three buffers rather than that many
 * objects.
 */
export class MapPathData {
  private _latitudes: Float64Array = new Float64Array(0);
  private _longitudes: Float64Array = new Float64Array(0);
  private _logTimes: BigUint64Array = new BigUint64Array(0);
  /**
   * The arrays above are allocated with room for later chunks, so their
   * `length` is the capacity rather than the point count. This is the point
   * count, and every read below stops at this index.
   */
  private _length = 0;
  private _isComplete = false;
  // Each extreme starts at the opposite infinity so the first coordinate
  // replaces it. An infinity that survives means no finite coordinate has been
  // appended on that axis, which is the condition getBounds reports as
  // "no bounds yet".
  private _minLat = Infinity;
  private _maxLat = -Infinity;
  private _minLon = Infinity;
  private _maxLon = -Infinity;
  /**
   * Where `selectIndicesWithin` collects its matches. Retained between calls
   * so that panning or zooming the map redraws a path's points without
   * allocating an array proportional to its length; at 4 bytes a sample that
   * costs a sixth of what the three coordinate arrays do. Entries past the
   * match count of the call in progress are leftovers from earlier calls.
   */
  private _matchIndexScratch = new Uint32Array(0);

  /** Number of points received so far. */
  get length(): number {
    return this._length;
  }

  /** Whether all data has been received */
  get isComplete(): boolean {
    return this._isComplete;
  }

  /**
   * Adds one chunk of the worker's coordinate stream to the end of the path
   * and folds it into the running bounds.
   *
   * Throws when the three arrays differ in length: they are parallel (index i
   * of each describes one point), so storing a mismatched chunk would
   * misalign every point past the shortest array's end.
   */
  appendChunk(
    latitudes: Float64Array,
    longitudes: Float64Array,
    logTimes: BigUint64Array,
  ): void {
    if (
      latitudes.length !== longitudes.length ||
      latitudes.length !== logTimes.length
    ) {
      throw new Error(
        `Coordinate chunk arrays must be the same length: got ${latitudes.length} latitudes, ${longitudes.length} longitudes, and ${logTimes.length} log times`,
      );
    }

    // Capacity doubles when a chunk overflows it, so loading a whole path
    // recopies each point about once in total. Growing the arrays by each
    // chunk's size instead recopies every point received so far on every
    // chunk, which costs time quadratic in the chunk count: at the worker's
    // 1000-point chunks, a 100k-point path copies about 5 million elements
    // per array.
    const filled = this._length;
    const required = filled + latitudes.length;

    if (required > this._latitudes.length) {
      let capacity = Math.max(this._latitudes.length, 1);
      while (capacity < required) {
        capacity *= 2;
      }

      const grownLatitudes = new Float64Array(capacity);
      grownLatitudes.set(this._latitudes.subarray(0, filled), 0);
      this._latitudes = grownLatitudes;

      const grownLongitudes = new Float64Array(capacity);
      grownLongitudes.set(this._longitudes.subarray(0, filled), 0);
      this._longitudes = grownLongitudes;

      const grownLogTimes = new BigUint64Array(capacity);
      grownLogTimes.set(this._logTimes.subarray(0, filled), 0);
      this._logTimes = grownLogTimes;
    }

    this._latitudes.set(latitudes, filled);
    this._longitudes.set(longitudes, filled);
    this._logTimes.set(logTimes, filled);
    this._length = required;

    // Fold the chunk into the running bounds. A NaN coordinate fails every
    // comparison below, so it is skipped instead of poisoning an extreme.
    for (let i = 0; i < latitudes.length; i++) {
      const lat = latitudes[i];
      const lon = longitudes[i];
      if (lat < this._minLat) {
        this._minLat = lat;
      }
      if (lat > this._maxLat) {
        this._maxLat = lat;
      }
      if (lon < this._minLon) {
        this._minLon = lon;
      }
      if (lon > this._maxLon) {
        this._maxLon = lon;
      }
    }
  }

  /**
   * Geographic extent of the coordinates appended so far, or undefined until a
   * finite coordinate has been appended on each axis. Longitude extremes are a
   * plain numeric min and max, so a path crossing the antimeridian yields
   * bounds that span the long way around the globe.
   */
  getBounds(): GeoBounds | undefined {
    if (!Number.isFinite(this._minLat) || !Number.isFinite(this._minLon)) {
      return undefined;
    }
    return {
      minLat: this._minLat,
      maxLat: this._maxLat,
      minLon: this._minLon,
      maxLon: this._maxLon,
    };
  }

  /**
   * Records that every point of the path has arrived, and warns when the
   * number received disagrees with the `totalCount` the worker reports.
   *
   * The arrays keep whatever room the last doubling left past the final
   * point. Trimming it would copy all three of them just as the user starts
   * interacting with the finished path, to recover under half the
   * allocation: about 0.75 MB for a 100k-point path.
   */
  markComplete(totalCount: number): void {
    this._isComplete = true;
    if (this._length !== totalCount) {
      LoggerService.warn(
        `MapPathData: Expected ${totalCount} points, but have ${this._length}`,
      );
    }
  }

  /**
   * Coordinate at `index`, or undefined when it falls outside the points
   * received so far.
   */
  getCoordinate(
    index: number,
  ): { lat: number; lon: number; logTime: bigint } | undefined {
    if (index < 0 || index >= this._length) {
      return undefined;
    }
    return {
      lat: this._latitudes[index],
      lon: this._longitudes[index],
      logTime: this._logTimes[index],
    };
  }

  /**
   * Index of the sample nearest a coordinate within the half-open range
   * `[fromIndex, toIndexExclusive)`, or undefined when that range holds no
   * received points. A click on a drawn point is resolved this way: the drawn
   * point stands for a run of samples, and the nearest one to the click is
   * the one the user meant.
   *
   * Distance is in squared degrees, with the longitude offset scaled by the
   * cosine of `lat`, the ratio Web Mercator draws between a degree of
   * longitude and a degree of latitude there. Candidates are therefore
   * ordered as they appear on screen, except where that cosine differs across
   * them, which takes both a near tie and candidates many degrees of latitude
   * apart.
   */
  nearestIndexTo(
    lat: number,
    lon: number,
    fromIndex: number,
    toIndexExclusive: number,
  ): number | undefined {
    // Clamp to the points received: the range is derived from the spacing
    // between drawn points, which can reach past the end of the path.
    const from = Math.max(0, fromIndex);
    const to = Math.min(this._length, toIndexExclusive);

    const lonScale = Math.cos((lat * Math.PI) / 180);
    let nearest: number | undefined;
    let nearestDistance = Infinity;
    for (let i = from; i < to; i++) {
      const dLat = this._latitudes[i] - lat;
      const dLon = (this._longitudes[i] - lon) * lonScale;
      const distance = dLat * dLat + dLon * dLon;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = i;
      }
    }

    return nearest;
  }

  /**
   * Index of the point whose log time is closest to `targetLogTime`, which is
   * how playback places a path's current-location marker. Log times ascend,
   * so this is a binary search. A target outside the path's time span
   * resolves to the first or last point; an empty path yields 0, an index at
   * which `getCoordinate` returns undefined.
   */
  findNearestIndex(targetLogTime: bigint): number {
    if (this._length === 0) {
      return 0;
    }

    let left = 0;
    let right = this._length - 1;

    if (targetLogTime <= this._logTimes[0]) {
      return 0;
    }
    if (targetLogTime >= this._logTimes[right]) {
      return right;
    }

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midTime = this._logTimes[mid];

      if (midTime === targetLogTime) {
        return mid;
      }

      if (midTime < targetLogTime) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    // The loop ended with right < left, straddling the target: right holds
    // the last time before it and left the first time after. Return whichever
    // of the two is closer.
    if (left >= this._length) {
      return right;
    }

    const leftDiff =
      this._logTimes[left] > targetLogTime
        ? this._logTimes[left] - targetLogTime
        : targetLogTime - this._logTimes[left];
    const rightDiff =
      this._logTimes[right] > targetLogTime
        ? this._logTimes[right] - targetLogTime
        : targetLogTime - this._logTimes[right];

    return leftDiff < rightDiff ? left : right;
  }

  /**
   * Indices of the samples whose coordinate falls inside `bounds`, thinned to
   * at most `budget` of them, which caps the number of point features the map
   * draws however long the path is. The result holds `min(budget, matches)`
   * indices spread evenly over the matches by ordinal, the first and last
   * match always among them when two or more are returned; matches that fit
   * inside the budget are all returned.
   *
   * `bounds` of `undefined` matches the whole path. NaN coordinates fail
   * every comparison and so never match. Longitude is compared numerically
   * against bounds a map reports unwrapped, which drops samples that are on
   * screen in two viewport shapes: one straddling the antimeridian (a west of
   * 170 and an east of 190) matches nothing stored on the far side of it, and
   * one wider than the world at low zoom keeps an unclamped center longitude,
   * so the window can exclude samples visible in a neighboring world copy.
   */
  selectIndicesWithin(bounds: GeoBounds | undefined, budget: number): number[] {
    if (budget <= 0 || this._length === 0) {
      return [];
    }

    const minLat = bounds?.minLat ?? -Infinity;
    const maxLat = bounds?.maxLat ?? Infinity;
    const minLon = bounds?.minLon ?? -Infinity;
    const maxLon = bounds?.maxLon ?? Infinity;

    // Collect the matches in one scan, so choosing which of them to keep is a
    // lookup by match ordinal instead of a second pass over every sample. The
    // buffer is sized to the coordinate arrays' capacity, so a path still
    // streaming in does not reallocate it on every refresh.
    if (this._matchIndexScratch.length < this._length) {
      this._matchIndexScratch = new Uint32Array(this._latitudes.length);
    }
    let matchCount = 0;
    for (let i = 0; i < this._length; i++) {
      const lat = this._latitudes[i];
      const lon = this._longitudes[i];
      if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
        this._matchIndexScratch[matchCount] = i;
        matchCount++;
      }
    }

    if (matchCount === 0) {
      return [];
    }

    const pickCount = Math.min(budget, matchCount);
    const selected: number[] = [];
    for (let pick = 0; pick < pickCount; pick++) {
      selected.push(
        this._matchIndexScratch[
          evenSpreadMatchOrdinal(pick, pickCount, matchCount)
        ],
      );
    }

    return selected;
  }

  /**
   * Coordinates over the half-open index range `[fromIndex, toIndexExclusive)`
   * as a GeoJSON-compatible array (for a Mapbox LineString). The range
   * defaults to every point received so far, and a caller splitting one line
   * across several features passes the span of each in turn.
   *
   * Indices outside the points received are clamped, so an overrunning range
   * yields fewer coordinates rather than undefined entries.
   */
  toGeoJSONCoordinates(
    fromIndex: number = 0,
    toIndexExclusive: number = this._length,
  ): [number, number][] {
    const start = Math.max(0, fromIndex);
    const end = Math.min(this._length, toIndexExclusive);
    const coords: [number, number][] = [];
    for (let i = start; i < end; i++) {
      // GeoJSON uses [lon, lat] order
      coords.push([this._longitudes[i], this._latitudes[i]]);
    }
    return coords;
  }
}

/**
 * The half-open range `[fromIndex, toIndexExclusive)` of sample indices that
 * the drawn point at position `drawnAt` of `selection` stands for: the samples
 * between its neighboring drawn points, which no other drawn point speaks for.
 * `selection` holds drawn sample indices ascending, as `selectIndicesWithin`
 * returns them, and `drawnAt` must be a position in it. Three cases:
 *
 * 1. An interior point covers the samples after the previous drawn point up to
 *    the next one.
 * 2. A point at either end of the selection reaches one point spacing beyond
 *    itself, the same reach it would have from a neighbor on that side, and no
 *    further. The selection covers the padded viewport, so samples farther out
 *    are off screen and a click on an end point cannot have meant them; the
 *    range spans at most the two gaps either side of the drawn point, however
 *    far the path runs beyond the selection.
 * 3. A single-point selection has no spacing to mirror, so its range is that
 *    one sample.
 *
 * `fromIndex` is never negative. `toIndexExclusive` may run past the samples
 * received so far, which `nearestIndexTo` clamps.
 */
export function representedSampleRange(
  selection: readonly number[],
  drawnAt: number,
): { fromIndex: number; toIndexExclusive: number } {
  const drawnIndex = selection[drawnAt];
  const previous = selection[drawnAt - 1];
  const next = selection[drawnAt + 1];

  let fromIndex: number;
  if (previous === undefined) {
    const spacingAbove = next === undefined ? 0 : next - drawnIndex;
    fromIndex = Math.max(0, drawnIndex - spacingAbove);
  } else {
    fromIndex = previous + 1;
  }

  let toIndexExclusive: number;
  if (next === undefined) {
    const spacingBelow = previous === undefined ? 0 : drawnIndex - previous;
    toIndexExclusive = drawnIndex + spacingBelow + 1;
  } else {
    toIndexExclusive = next;
  }

  return { fromIndex, toIndexExclusive };
}

/**
 * Ordinal of the match to take as the `pick`-th of `pickCount` picks spread
 * evenly over `matchCount` matches, with `pickCount <= matchCount`. Two or
 * more picks always take the first and last match, and the gaps between
 * neighboring picks are as even as whole ordinals allow, differing by at most
 * one; a single pick takes the first match. Successive picks never land on the
 * same ordinal, so `pickCount` picks yield that many distinct matches.
 */
function evenSpreadMatchOrdinal(
  pick: number,
  pickCount: number,
  matchCount: number,
): number {
  if (pickCount === 1) {
    return 0;
  }
  return Math.round((pick * (matchCount - 1)) / (pickCount - 1));
}
